import { describeWebSocketCloseCode } from '../ws-close-diagnostics.js';

/** Mutable outage state shared by the bridge runtime and unit tests. */
export interface ReconnectOutageTracker {
  startedAt: number | null;
  totalFailures: number;
  /** Highest delay tier index for which we've emitted a promotion log (-1 = none yet). */
  lastLoggedTierIndex: number;
  originalCloseCode: number | null;
  originalCloseReason: string | null;
}

export function createEmptyReconnectOutageTracker(): ReconnectOutageTracker {
  return {
    startedAt: null,
    totalFailures: 0,
    lastLoggedTierIndex: -1,
    originalCloseCode: null,
    originalCloseReason: null,
  };
}

export function resetReconnectOutageTracker(tracker: ReconnectOutageTracker): void {
  tracker.startedAt = null;
  tracker.totalFailures = 0;
  tracker.lastLoggedTierIndex = -1;
  tracker.originalCloseCode = null;
  tracker.originalCloseReason = null;
}

export function reconnectTierIndexForOutageElapsedMs(elapsedMs: number): 0 | 1 | 2 | 3 {
  if (elapsedMs < 60_000) return 0;
  if (elapsedMs < 360_000) return 1;
  if (elapsedMs < 1_200_000) return 2;
  return 3;
}

export function reconnectDelayMsForOutageElapsedMs(elapsedMs: number): number {
  if (elapsedMs < 60_000) return 3000;
  if (elapsedMs < 360_000) return 10_000;
  if (elapsedMs < 1_200_000) return 20_000;
  return 60_000;
}

export interface TierPromotionLogParts {
  serviceLabel: string;
  disconnectLeadIn: string;
  code: number;
  reason: string;
  totalFailures: number;
}

function disconnectDetail(code: number, reason: string): string {
  const r = reason.trim();
  const reasonPart = r ? ` reason="${r}"` : '';
  return `code=${code} (${describeWebSocketCloseCode(code)})${reasonPart}`;
}

export function formatTierPromotionLogLine(parts: TierPromotionLogParts): string {
  const detail = disconnectDetail(parts.code, parts.reason);
  return `${parts.serviceLabel} ${parts.disconnectLeadIn}: ${detail}. Trying to re-establish a connection in the background (retry attempts so far: ${parts.totalFailures}).`;
}

/**
 * Record one failed reconnect attempt and compute the delay before the next try.
 * When the outage crosses into a higher delay tier than we've logged, returns a log line.
 */
export function planReconnectAfterFailure(
  tracker: ReconnectOutageTracker,
  nowMs: number,
  serviceLabel: string,
  disconnectLeadIn: string,
  closeCode: number | undefined,
  closeReason: string | undefined,
): { delayMs: number; logLine: string | null } {
  if (tracker.startedAt == null) {
    tracker.startedAt = nowMs;
  }
  if (closeCode != null && tracker.originalCloseCode == null) {
    tracker.originalCloseCode = closeCode;
    tracker.originalCloseReason = closeReason ?? '';
  }
  tracker.totalFailures += 1;

  const elapsed = nowMs - tracker.startedAt;
  const tier = reconnectTierIndexForOutageElapsedMs(elapsed);
  const delayMs = reconnectDelayMsForOutageElapsedMs(elapsed);

  let logLine: string | null = null;
  if (tier > tracker.lastLoggedTierIndex) {
    tracker.lastLoggedTierIndex = tier;
    const code = tracker.originalCloseCode ?? closeCode ?? 0;
    const reason = tracker.originalCloseReason ?? closeReason ?? '';
    logLine = formatTierPromotionLogLine({
      serviceLabel,
      disconnectLeadIn,
      code,
      reason,
      totalFailures: tracker.totalFailures,
    });
  }

  return { delayMs, logLine };
}
